import Resource from 'rest-resource'
import axios from 'axios'
import { parse as urlParse } from 'url'
import { parse as queryParse } from 'querystring'
import { BaseClient } from 'rest-resource/dist/client'
import { range, flatten } from 'lodash'
import assert from 'assert'
import { API_BASE_URL } from '../config'

export function getTokenPayload(token) {
    try {
        let jwtPieces = token.split('.')
        let payloadBase64 = jwtPieces[1]
        let payloadBuffer = Buffer.from(payloadBase64, 'base64').toString()
        return JSON.parse(payloadBuffer.toString())
    } catch (e) {
        return undefined
    }
}

export class Client extends BaseClient {
    constructor(baseURL, token, options) {
        super(baseURL, options)
        if (token) {
            this._token = token
        }
    }

    negotiateContent(ResourceClass, negotioatorOptions = {}) {
        return (response) => {
            if (PagedContentNegotiator.isListResponse(response)) {
                return new PagedContentNegotiator(ResourceClass, response, negotioatorOptions)
            } else {
                return new DefaultContentNegotiator(ResourceClass, response, negotioatorOptions)
            }
        }
    }

    get token() {
        return this._token
    }

    set token(value) {
        if (!this.config.headers) {
            this.config.headers = {}
        }

        this._token = value
        this._tokenPayload = getTokenPayload(value)
        this._tokenExpires = new Date(this._tokenPayload.exp * 1000)
        this._tokenNextCheckTime = new Date((this._tokenPayload.exp - 3600) * 1000)
        this.config.headers['Authorization'] = `Bearer ${value}`
        this.axios = axios.create(this.config)
    }

    get axios() {
        if(!this._axios) {
            this._axios = axios.create(this.config)
        }

        return this._axios
    }

    set axios(value) {
        this._axios = value
        this._axios.interceptors.request.use((request) => this.onRequest(request))
    }

    getTokenPayload() {
        return this._tokenPayload
    }

    tokenIsExpired() {
        try {
            let nowInSeconds = Math.floor(Date.now() / 1000)
            return this._tokenPayload.exp < nowInSeconds
        } catch (e) {
            return true
        }
    }

    tokenIsValid() {
        return this.token && !this.tokenIsExpired()
    }

    tokenBecomesExpired() {
        // Do nothing...
    }

    tokenWillExpire() {
        this._token = undefined
    }

    onRequest(request) {
        if(new Date() > this._tokenNextCheckTime) {
            this.tokenWillExpire()
        }

        return request
    }

    onError(e) {
        if (e && e.response && e.response && e.response.data.detail && e.response.data.detail.match('expired')) {
            this.tokenBecomesExpired()
        }

        throw e
    }
}

export class DefaultContentNegotiator {
    resources = []

    constructor(ResourceClass, response, options = {}) {
        this.ResourceClass = ResourceClass
        this.response = response
        this.body = this.response.data
        this.urlInfo = urlParse(response.config.url)
        this.options = options

        if('object' === typeof response.config.params) {
            this.query = response.config.params
        } else if ('string' === typeof this.urlInfo.query) {
            this.query = queryParse(this.urlInfo.query) || {}
        } else {
            this.query = {}
        }

        this.parseResources()
    }

    parseResources() {
        const ResourceClass = this.ResourceClass
        this.resources.push(new ResourceClass(this.body))
    }

    get listFn() {
        if('function' === typeof this.options.listFn) {
            return this.options.listFn.bind(this.ResourceClass)
        }

        return this.ResourceClass.list.bind(this.ResourceClass)
    }
}

export class PagedContentNegotiator extends DefaultContentNegotiator {
    constructor(ResourceClass, response, options = {}) {
        super(ResourceClass, response, options)
        assert(this.body.results, 'Can\'t create PageResponse instance without "results" property in body')
        this.count = Number(this.body.count)
        this.pages = Number(this.body.page_count)
        this.pageSize = Number(this.body.page_size)
        this.page = Number(this.query.page) || 1
    }

    static isListResponse(response) {
        return response.data && response.data.results
    }

    assertPageSize(page) {
        assert(page >= 1 && page <= this.pages, `Page size must be between 1 and ${this.pages} (received ${page})`)
    }

    parseResources() {
        const ResourceClass = this.ResourceClass
        for (let i = 0; i < this.body.results.length; i++) {
            this.resources.push(new ResourceClass(this.body.results[i]))
        }
    }

    nextPage() {
        const query = Object.assign({}, this.query, { page: this.page + 1 })
        this.assertPageSize(query.page)
        this.options.params = query
        return this.listFn(this.options)
    }

    prevPage() {
        const query = Object.assign({}, this.query, { page: this.page - 1 })
        this.assertPageSize(query.page)
        this.options.params = query
        return this.listFn(this.options)
    }

    goToPage(pageNumber) {
        const query = Object.assign({}, this.query, { page: pageNumber })
        this.assertPageSize(query.page)
        this.options.params = query
        return this.listFn(this.options)
    }

    otherPageNumbers() {
        return range(1, this.pages + 1, 1).filter((page) => page != this.page)
    }
}

export const client = new Client(API_BASE_URL)

if (localStorage.getItem('token')) {
    client.token = localStorage.getItem('token')
}

export default class BaseResource extends Resource {
    static client = client
    static allOnce = false // If set to true, Resource.all() will only be called once per page load
    static allLoaded = false // Flag for allOnce functionality

    static async all(options = {}) {
        if (this.allLoaded) {
            // Resource.all() has already resolved, just return cached resources
            let resources = []
            for (let resourceKey in this.cache) {
                resources.push(this.cache[resourceKey].resource)
            }

            return resources
        }

        let content = await this.list(options)
        let otherPages = content.otherPageNumbers()

        let allResources = {
            [content.page]: content.resources,
        }

        let promises = otherPages.map((page) => {
            let thisPageQuery = Object.assign({}, content.query, { page })
            return this.list(Object.assign({}, options, { query: thisPageQuery })).then((res) => {
                allResources[page] = res.resources
            })
        })

        await Promise.all(promises)

        if (this.allOnce) {
            // If allOnce is set, don't need to remote GET Resource.all() again
            this.allLoaded = true
        }

        return flatten(Object.values(allResources))
    }

    static filter(filterArgs) {
        return this.list({ query: filterArgs })
    }

    static filterAll(filterArgs, options = {}) {
        let opts = Object.assign({}, { query: filterArgs }, options)
        return this.all(opts)
    }

    static search(searchString, filterArgs = {}) {
        let query = Object.assign(filterArgs, { search: searchString })
        return this.list({ query })
    }

    static searchAll(searchString, filterArgs = {}) {
        let query = Object.assign(filterArgs, { search: searchString })
        return this.all({ query })
    }

    get readonly() {
        return this.attributes.readonly_values || {}
    }

    async mapResolve(attrMap) {
        let attributes = {}
        for (let key in attrMap) {
            let mapKey = attrMap[key]
            attributes[mapKey] = await this.resolveAttribute(key)
        }
        
        return attributes
    }

    toString() {
        if (this.attributes.name) {
            return this.attributes.name
        }

        return super.toString()
    }
}
